const ATTRIBUTE = 'vuit_whatsapp_consent_token';
const COPY_VERSION = 'vuit-wa-order-updates-2026-10-26';

export function normalizeUaePhone(value) {
  let phone = String(value ?? '').trim().replace(/[\s().-]/g, '');
  if (phone.startsWith('00971')) phone = `+971${phone.slice(5)}`;
  else if (phone.startsWith('971')) phone = `+${phone}`;
  else if (/^05\d{8}$/.test(phone)) phone = `+971${phone.slice(1)}`;
  return /^\+9715[024568]\d{7}$/.test(phone) ? phone : null;
}

export function createConsentController(transport) {
  let state = { phase: 'loading', checked: false, token: '', maskedPhone: '', message: 'loading', pending: true };
  let currentCartToken = '';
  let operation = null;
  const subscribers = new Set();
  const emit = (patch) => { state = { ...state, ...patch }; for (const fn of subscribers) fn({ ...state }); };
  const clearCart = () => transport.setCartToken('').catch(() => undefined);
  const load = async () => {
    emit({ phase: 'loading', message: 'loading', pending: true });
    try {
      const cart = await transport.getCart();
      currentCartToken = cart.token;
      const token = String(cart.attributes?.[ATTRIBUTE] || '');
      if (!token) {
        emit({ phase: 'off', checked: false, token: '', maskedPhone: '', message: 'off', pending: false });
        return;
      }
      emit({ token });
      const receipt = await transport.status(token, currentCartToken);
      if (receipt.active === true) {
        emit({ phase: 'saved', checked: true, token, maskedPhone: receipt.maskedPhone || '', message: 'saved', pending: false });
      } else {
        await clearCart();
        emit({ phase: 'off', checked: false, token: '', maskedPhone: '', message: 'expired', pending: false });
      }
    } catch {
      emit({ phase: 'unknown', message: 'unknown', pending: false });
    }
  };
  const request = async (checked, rawPhone) => {
    if (checked === state.checked && state.phase !== 'error') return;
    if (!checked) {
      emit({ phase: 'saving', message: 'saving', pending: true });
      try {
        if (state.token) await transport.revoke(state.token);
        emit({ phase: 'off', checked: false, token: '', maskedPhone: '', message: 'off', pending: true });
        await clearCart();
        emit({ pending: false });
      } catch {
        emit({ phase: 'error', message: 'error', pending: false });
      }
      return;
    }
    const phone = normalizeUaePhone(rawPhone);
    if (!phone) { emit({ phase: 'error', message: 'invalid', pending: false }); return; }
    emit({ phase: 'saving', message: 'saving', pending: true });
    let issuedToken = '';
    try {
      const cart = await transport.getCart();
      currentCartToken = cart.token;
      const receipt = await transport.register(currentCartToken, phone);
      if (!/^[A-Za-z0-9_-]{32,128}$/.test(receipt.token || '')) throw new Error('Invalid consent receipt');
      issuedToken = receipt.token;
      await transport.setCartToken(issuedToken);
      emit({ phase: 'saved', checked: true, token: issuedToken, maskedPhone: `+971 •• ••• ${phone.slice(-4)}`, message: 'saved', pending: false });
    } catch {
      let revoked = true;
      if (issuedToken) {
        try { await transport.revoke(issuedToken); } catch { revoked = false; }
      }
      if (revoked) {
        await clearCart();
        emit({ phase: 'error', checked: false, token: '', maskedPhone: '', message: 'error', pending: false });
      } else {
        emit({ phase: 'unknown', checked: false, token: issuedToken, maskedPhone: '', message: 'unknown', pending: false });
      }
    }
  };
  const run = (fn) => {
    if (operation) return operation;
    operation = fn().finally(() => { operation = null; });
    return operation;
  };
  return {
    subscribe(fn) { subscribers.add(fn); fn({ ...state }); return () => subscribers.delete(fn); },
    getState() { return { ...state }; },
    load() { return run(load); },
    request(checked, phone) { return run(() => request(checked, phone)); },
  };
}

export function createTransport(element, fetcher = fetch) {
  const endpoint = new URL(element.dataset.endpoint);
  if (endpoint.origin !== 'https://gpt-automation-hub-production.up.railway.app' || endpoint.pathname !== '/vuit/whatsapp/consents') throw new Error('Unsupported consent endpoint');
  const request = async (url, options = {}) => {
    const response = await fetcher(url, { ...options, signal: AbortSignal.timeout(10000) });
    if (!response.ok) throw new Error('Preference update unavailable');
    return response.json();
  };
  const post = (path, body) => request(`${endpoint.href}${path}`, {
    method: 'POST', mode: 'cors', credentials: 'omit', cache: 'no-store',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  return {
    getCart: () => request(element.dataset.cartUrl, { credentials: 'same-origin', cache: 'no-store' }),
    setCartToken: async (token) => {
      const result = await request(element.dataset.updateUrl, {
      method: 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: { [ATTRIBUTE]: token } }),
      });
      if (String(result.attributes?.[ATTRIBUTE] || '') !== token) throw new Error('Preference was not saved');
      return result;
    },
    register: (cartToken, phone) => post('', { cartToken, phone, copyVersion: COPY_VERSION, locale: element.dataset.locale === 'ar' ? 'ar' : 'en', affirmative: true }),
    revoke: async (token) => {
      const result = await post('/revoke', { token });
      if (result.revoked !== true) throw new Error('Revocation not acknowledged');
      return result;
    },
    status: async (token, cartToken) => {
      const result = await post('/status', { token, cartToken });
      if (typeof result.active !== 'boolean') throw new Error('Preference status unavailable');
      return result;
    },
  };
}

if (typeof window !== 'undefined' && typeof customElements !== 'undefined' && !customElements.get('vuit-whatsapp-consent')) {
  let controller;
  class VuitWhatsappConsent extends HTMLElement {
    connectedCallback() {
      if (this.unsubscribe) return;
      this.phone = this.querySelector('[data-wa-phone]');
      this.choice = this.querySelector('[data-wa-choice]');
      this.status = this.querySelector('[data-wa-status]');
      this.tokenInput = this.querySelector('[data-wa-token]');
      this.retry = this.querySelector('[data-wa-retry]');
      if (!this.phone || !this.choice || !this.status || !this.tokenInput || !this.retry) return;
      if (!controller) {
        controller = createConsentController(createTransport(this));
      }
      this.unsubscribe = controller.subscribe((state) => {
        this.dataset.state = state.phase;
        this.choice.checked = state.checked;
        this.choice.indeterminate = state.phase === 'unknown';
        this.choice.disabled = state.pending || state.phase === 'unknown';
        this.phone.disabled = state.pending || state.checked || state.phase === 'unknown';
        if (state.checked && state.maskedPhone) this.phone.value = state.maskedPhone;
        else if (this.phone.value.includes('•')) this.phone.value = '';
        this.tokenInput.value = state.checked && state.phase !== 'unknown' ? state.token : '';
        this.status.textContent = this.dataset[state.message] || this.dataset.error;
        this.retry.hidden = state.phase !== 'unknown';
        this.setAttribute('aria-busy', state.pending ? 'true' : 'false');
      });
      this.onChoice = () => {
        const requested = this.choice.checked;
        this.choice.checked = controller.getState().checked;
        controller.request(requested, this.phone.value);
      };
      this.onRetry = () => controller.load();
      this.choice.addEventListener('change', this.onChoice);
      this.retry.addEventListener('click', this.onRetry);
      // Reconcile section re-renders with the current cart; concurrent requests coalesce.
      controller.load();
    }
    disconnectedCallback() {
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.choice?.removeEventListener('change', this.onChoice);
      this.retry?.removeEventListener('click', this.onRetry);
    }
  }
  customElements.define('vuit-whatsapp-consent', VuitWhatsappConsent);
}
